import {
  Box,
  TextField,
  Typography,
  Divider,
  MenuItem,
  Chip,
  Paper,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  convertTime,
  resultCopyTapped,
  selectState,
  sourceTimeChanged,
  sourceTimezoneChanged,
  targetTimezoneChanged,
  resetToNow,
} from "./TimezoneConverterSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { ResultCard } from "../../components/result_card/ResultCard";
import EventIcon from "@mui/icons-material/Event";
import { HomeCard } from "../../components/home_card/HomeCard";
import DateCalculatorImage from "../../assets/DateCalculator.webp";
import CountdownTimerImage from "../../assets/CountdownTimer.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function TimezoneConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  const handleConvert = () => {
    dispatch(convertTime());
  };

  const handleCopyResult = () => {
    if (state.convertedTime) {
      navigator.clipboard.writeText(state.convertedTime);
      dispatch(resultCopyTapped());
    }
  };

  const handleResetToNow = () => {
    dispatch(resetToNow());
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("timezoneConverter.title")}
      pageSeoTitle={t("timezoneConverter.seoTitle")}
      pageDescription={t("timezoneConverter.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("timezoneConverter.mainHeading")}
          </Typography>
        </Grid2>

        {/* Popular Timezone Chips */}
        <Grid2 xs={12}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 3,
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("timezoneConverter.popularTimezones")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
              {[
                { name: "UTC", description: "UTC+0" },
                { name: "EST", description: "GMT-5" },
                { name: "PST", description: "GMT-8" },
                { name: "CET", description: "GMT+1" },
                { name: "JST", description: "GMT+9" },
                { name: "AEST", description: "GMT+10" },
                { name: "IST", description: "GMT+5:30" },
                { name: "SAST", description: "GMT+2" },
              ].map((tz) => (
                <Chip
                  key={tz.name}
                  label={`${tz.name} (${tz.description})`}
                  onClick={() => dispatch(targetTimezoneChanged(tz.name))}
                  variant={
                    state.targetTimezone === tz.name ? "filled" : "outlined"
                  }
                  color={
                    state.targetTimezone === tz.name ? "primary" : "default"
                  }
                  sx={{
                    cursor: "pointer",
                    color:
                      state.targetTimezone === tz.name
                        ? "secondary.main"
                        : "white",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        </Grid2>

        {/* Source Timezone */}
        <Grid2 xs={6}>
          <CustomSelect
            label={t("timezoneConverter.fromLabel")}
            value={state.sourceTimezone}
            onChange={(e) => dispatch(sourceTimezoneChanged(e.target.value))}
          >
            {state.timezones.map((timezone) => (
              <MenuItem key={timezone.name} value={timezone.name}>
                {timezone.name} ({timezone.offset}) - {timezone.description}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid2>

        {/* Target Timezone */}
        <Grid2 xs={6}>
          <CustomSelect
            label={t("timezoneConverter.toLabel")}
            value={state.targetTimezone}
            onChange={(e) => dispatch(targetTimezoneChanged(e.target.value))}
          >
            {state.timezones.map((timezone) => (
              <MenuItem key={timezone.name} value={timezone.name}>
                {timezone.name} ({timezone.offset}) - {timezone.description}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid2>

        {/* Source Time */}
        <Grid2 xs={12}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              fullWidth
              label={t("timezoneConverter.timeLabel")}
              type="datetime-local"
              variant="outlined"
              color="info"
              value={state.sourceTime}
              onChange={(e) => dispatch(sourceTimeChanged(e.target.value))}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <CustomButton
              onClick={handleResetToNow}
              label={t("timezoneConverter.nowButton")}
              icon={<EventIcon sx={{ color: "#FFD9A8" }} />}
            />
          </Box>
        </Grid2>

        {/* Convert Button */}
        <Grid2 xs={12} sx={{ mt: 2, mb: 2 }}>
          <CustomButton
            onClick={handleConvert}
            label={t("timezoneConverter.convertButton")}
          />
        </Grid2>

        {/* Result */}
        {state.convertedTime && (
          <Grid2 xs={12}>
            <ResultCard
              symbol=""
              description={t("timezoneConverter.resultTitle")}
              value={state.convertedTime}
              onCopy={handleCopyResult}
              resultCopied={state.resultCopied}
            />
          </Grid2>
        )}

        <Grid2 xs={12}>
          <Divider sx={{ my: 4 }} />
        </Grid2>

        {/* Content Section */}
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("timezoneConverter.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("timezoneConverter.content.freeDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("timezoneConverter.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("timezoneConverter.content.howToDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("timezoneConverter.content.timezonesHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("timezoneConverter.content.timezonesDescription")}
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              {t("timezoneConverter.content.popularTimezonesHeading")}
            </Typography>
            <Grid2 container spacing={2}>
              {[
                { name: "UTC", description: "Coordinated Universal Time" },
                { name: "EST", description: "Eastern Standard Time (GMT-5)" },
                { name: "PST", description: "Pacific Standard Time (GMT-8)" },
                { name: "CET", description: "Central European Time (GMT+1)" },
                { name: "JST", description: "Japan Standard Time (GMT+9)" },
                {
                  name: "AEST",
                  description: "Australian Eastern Standard Time (GMT+10)",
                },
              ].map((tz) => (
                <Grid2 xs={12} sm={6} md={4} key={tz.name}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color={"secondary"}
                      fontWeight="bold"
                    >
                      {tz.name}
                    </Typography>
                    <Typography variant="body1">{tz.description}</Typography>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("temperatureConverter.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="dateCalculator"
            link="/DateCalculator"
            smallCard={true}
            image={DateCalculatorImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="countdownTimer"
            link="/CountdownTimer"
            smallCard={true}
            image={CountdownTimerImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
